import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable({
    providedIn: 'root'
})
export class PlatformService {
    constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

    /**
     * Check if the current platform is browser
     */
    isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    /**
     * Check if the current platform is server
     */
    isServer(): boolean {
        return isPlatformServer(this.platformId);
    }

    /**
     * Execute code only in browser environment
     * @param callback Function to execute in browser
     * @param fallback Optional fallback for server
     */
    runInBrowser<T>(callback: () => T, fallback?: () => T): T | undefined {
        if (this.isBrowser()) {
            return callback();
        } else if (fallback) {
            return fallback();
        }
        return undefined;
    }

    /**
     * Execute code only in server environment
     * @param callback Function to execute in server
     */
    runInServer<T>(callback: () => T): T | undefined {
        if (this.isServer()) {
            return callback();
        }
        return undefined;
    }

    /**
     * Get window object safely (browser-only)
     */
    getWindow(): Window | undefined {
        return this.isBrowser() ? window : undefined;
    }

    /**
     * Get document object safely (browser-only)
     */
    getDocument(): Document | undefined {
        return this.isBrowser() ? document : undefined;
    }

    /**
     * Check if a specific feature is available (e.g., localStorage, sessionStorage)
     */
    isFeatureAvailable(feature: string): boolean {
        if (!this.isBrowser()) {
            return false;
        }

        try {
            switch (feature.toLowerCase()) {
                case 'localstorage':
                    return typeof localStorage !== 'undefined';
                case 'sessionstorage':
                    return typeof sessionStorage !== 'undefined';
                case 'indexeddb':
                    return typeof indexedDB !== 'undefined';
                default:
                    return typeof (window as any)[feature] !== 'undefined';
            }
        } catch (error) {
            return false;
        }
    }

    /**
     * Get user agent string (browser-only)
     */
    getUserAgent(): string | undefined {
        return this.runInBrowser(() => navigator.userAgent);
    }

    /**
     * Check if the user is on a mobile device (browser-only)
     */
    isMobile(): boolean {
        const userAgent = this.getUserAgent();
        if (!userAgent) return false;

        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    }
}
